import client from 'client'
import type {
  GetLatestTimeSeriesDataV2ReportTimeSeriesLatestPostMutationRequest,
  GetLatestTimeSeriesDataV2ReportTimeSeriesLatestPostMutationResponse,
  GetLatestTimeSeriesDataV2ReportTimeSeriesLatestPostQueryParams,
  GetLatestTimeSeriesDataV2ReportTimeSeriesLatestPost422,
} from '../../types/GetLatestTimeSeriesDataV2ReportTimeSeriesLatestPost.ts'
import type { QueryKey, QueryObserverOptions, UseQueryResult } from '@tanstack/react-query'
import type { RequestConfig, ResponseErrorConfig, ResponseConfig } from 'client'
import { queryOptions, useQuery } from '@tanstack/react-query'

export const getLatestTimeSeriesDataV2ReportTimeSeriesLatestPostQueryKey = (
  data?: GetLatestTimeSeriesDataV2ReportTimeSeriesLatestPostMutationRequest,
  params?: GetLatestTimeSeriesDataV2ReportTimeSeriesLatestPostQueryParams,
) => [{ url: '/report/time_series/latest' }, ...(params ? [params] : []), ...(data ? [data] : [])] as const

export type GetLatestTimeSeriesDataV2ReportTimeSeriesLatestPostQueryKey = ReturnType<typeof getLatestTimeSeriesDataV2ReportTimeSeriesLatestPostQueryKey>

/**
 * @summary Get Latest Time Series Data V2
 * {@link /report/time_series/latest}
 */
export async function getLatestTimeSeriesDataV2ReportTimeSeriesLatestPost(
  data?: GetLatestTimeSeriesDataV2ReportTimeSeriesLatestPostMutationRequest,
  params?: GetLatestTimeSeriesDataV2ReportTimeSeriesLatestPostQueryParams,
  config: Partial<RequestConfig<GetLatestTimeSeriesDataV2ReportTimeSeriesLatestPostMutationRequest>> & { client?: typeof client } = {},
) {
  const { client: request = client, ...requestConfig } = config

  const res = await request<
    GetLatestTimeSeriesDataV2ReportTimeSeriesLatestPostMutationResponse,
    ResponseErrorConfig<GetLatestTimeSeriesDataV2ReportTimeSeriesLatestPost422>,
    GetLatestTimeSeriesDataV2ReportTimeSeriesLatestPostMutationRequest
  >({ method: 'POST', url: `/report/time_series/latest`, params, data, ...requestConfig })
  return res
}

export function getLatestTimeSeriesDataV2ReportTimeSeriesLatestPostQueryOptions(
  data?: GetLatestTimeSeriesDataV2ReportTimeSeriesLatestPostMutationRequest,
  params?: GetLatestTimeSeriesDataV2ReportTimeSeriesLatestPostQueryParams,
  config: Partial<RequestConfig<GetLatestTimeSeriesDataV2ReportTimeSeriesLatestPostMutationRequest>> & { client?: typeof client } = {},
) {
  const queryKey = getLatestTimeSeriesDataV2ReportTimeSeriesLatestPostQueryKey(data, params)
  return queryOptions<
    ResponseConfig<GetLatestTimeSeriesDataV2ReportTimeSeriesLatestPostMutationResponse>,
    ResponseErrorConfig<GetLatestTimeSeriesDataV2ReportTimeSeriesLatestPost422>,
    ResponseConfig<GetLatestTimeSeriesDataV2ReportTimeSeriesLatestPostMutationResponse>,
    typeof queryKey
  >({
    queryKey,
    queryFn: async ({ signal }) => {
      config.signal = signal
      return getLatestTimeSeriesDataV2ReportTimeSeriesLatestPost(data, params, config)
    },
  })
}

/**
 * @summary Get Latest Time Series Data V2
 * {@link /report/time_series/latest}
 */
export function useGetLatestTimeSeriesDataV2ReportTimeSeriesLatestPost<
  TData = ResponseConfig<GetLatestTimeSeriesDataV2ReportTimeSeriesLatestPostMutationResponse>,
  TQueryData = ResponseConfig<GetLatestTimeSeriesDataV2ReportTimeSeriesLatestPostMutationResponse>,
  TQueryKey extends QueryKey = GetLatestTimeSeriesDataV2ReportTimeSeriesLatestPostQueryKey,
>(
  data?: GetLatestTimeSeriesDataV2ReportTimeSeriesLatestPostMutationRequest,
  params?: GetLatestTimeSeriesDataV2ReportTimeSeriesLatestPostQueryParams,
  options: {
    query?: Partial<
      QueryObserverOptions<
        ResponseConfig<GetLatestTimeSeriesDataV2ReportTimeSeriesLatestPostMutationResponse>,
        ResponseErrorConfig<GetLatestTimeSeriesDataV2ReportTimeSeriesLatestPost422>,
        TData,
        TQueryData,
        TQueryKey
      >
    >
    client?: Partial<RequestConfig<GetLatestTimeSeriesDataV2ReportTimeSeriesLatestPostMutationRequest>> & { client?: typeof client }
  } = {},
) {
  const { query: queryOptions, client: config = {} } = options ?? {}
  const queryKey = queryOptions?.queryKey ?? getLatestTimeSeriesDataV2ReportTimeSeriesLatestPostQueryKey(data, params)

  const query = useQuery({
    ...(getLatestTimeSeriesDataV2ReportTimeSeriesLatestPostQueryOptions(data, params, config) as unknown as QueryObserverOptions),
    queryKey,
    ...(queryOptions as unknown as Omit<QueryObserverOptions, 'queryKey'>),
  }) as UseQueryResult<TData, ResponseErrorConfig<GetLatestTimeSeriesDataV2ReportTimeSeriesLatestPost422>> & { queryKey: TQueryKey }

  query.queryKey = queryKey as TQueryKey

  return query
}